import { Injectable } from '@angular/core';
import { Device } from '@capacitor/device';

export interface BatteryCheckResult {
  shouldShowAlert: boolean;
  level: number;
  isCharging: boolean;
}

/**
 * Service for checking device battery level and determining if low battery alert should be displayed.
 * Threshold: Battery level below 20% triggers alert (shown once per session).
 */
@Injectable({
  providedIn: 'root'
})
export class BatteryUtilsService {
  /**
   * Determines if low battery alert should be shown based on battery level and session state.
   * @param batteryLevel Battery level as decimal (0.0 to 1.0)
   * @param isCharging Whether device is currently charging
   * @param isAlreadyChecked Whether battery check was already performed in current session
   * @returns BatteryCheckResult indicating if alert should be shown and battery information
   */
  checkBatteryLevel(
    batteryLevel: number,
    isCharging: boolean,
    isAlreadyChecked: boolean
  ): BatteryCheckResult {
    const level = batteryLevel * 100;

    return {
      shouldShowAlert: level < 20 && !isAlreadyChecked,
      level,
      isCharging
    };
  }

  /**
   * Retrieves battery information from device and determines if alert should be shown.
   * @param isAlreadyChecked Whether battery check was already performed in current session
   * @returns Promise resolving to BatteryCheckResult
   */
  async checkBattery(isAlreadyChecked: boolean): Promise<BatteryCheckResult> {
    try {
      const batteryInfo = await Device.getBatteryInfo();

      if (!batteryInfo || batteryInfo.batteryLevel === undefined) {
        return {
          shouldShowAlert: false,
          level: 100,
          isCharging: false
        };
      }

      return this.checkBatteryLevel(
        batteryInfo.batteryLevel,
        batteryInfo.isCharging || false,
        isAlreadyChecked
      );
    } catch (error) {
      console.error('Failed to get battery information:', error);
      return {
        shouldShowAlert: false,
        level: 100,
        isCharging: false
      };
    }
  }
}
